import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { PencomConstants } from "@/constants/PencomConstants";

interface CertificateDownloadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isConfirming?: boolean;
}

const STORAGE_KEY = "pencom_cert_disclaimer_ack";

interface StoredAck {
  acknowledgedAt: number;
  version: string;
}

/** Returns true when a valid acknowledgment exists within the suppression window. */
export function hasCertificateDisclaimerAck(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const stored: StoredAck = JSON.parse(raw);
    if (stored.version !== PencomConstants.CERTIFICATE_DISCLAIMER_VERSION)
      return false;
    const suppressMs =
      PencomConstants.CERTIFICATE_DISCLAIMER_SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
    return Date.now() - stored.acknowledgedAt < suppressMs;
  } catch {
    return false;
  }
}

/** Persists an acknowledgment to localStorage. */
export function setCertificateDisclaimerAck(): void {
  const payload: StoredAck = {
    acknowledgedAt: Date.now(),
    version: PencomConstants.CERTIFICATE_DISCLAIMER_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function CertificateDownloadDialog({
  open,
  onOpenChange,
  onConfirm,
  isConfirming = false,
}: CertificateDownloadDialogProps) {
  const [checked, setChecked] = useState(false);

  // Reset checkbox every time the dialog opens
  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50" />
        <Dialog.Content
          aria-modal="true"
          className="fixed left-[50%] top-[50%] z-50 w-full max-w-[519px] translate-x-[-50%] translate-y-[-50%] bg-white rounded-2xl shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] focus:outline-none"
        >
          <div className="flex flex-col p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="font-clash-display font-semibold text-[20px] text-[#121212] leading-normal">
                Before You Download
              </Dialog.Title>
              <Dialog.Close
                className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity"
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="sr-only">Close</span>
              </Dialog.Close>
            </div>

            {/* Disclaimer text */}
            <div className="bg-[#FFF8EC] border border-[#F5A623] rounded-lg p-4 mb-6">
              <p className="font-clash-display text-xs font-semibold text-[#B45309] uppercase tracking-wide mb-2">
                Disclaimer
              </p>
              {PencomConstants.CERTIFICATE_DISCLAIMER_TEXT.map((line, i) => (
                <p
                  key={i}
                  className="font-clash-display text-sm text-[#404040] leading-[22px] mt-2"
                >
                  {line}
                </p>
              ))}
            </div>

            {/* Acknowledgment checkbox */}
            <label className="flex items-start gap-x-3 mb-8 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-primary-button cursor-pointer"
              />
              <span className="font-clash-display text-sm text-[#404040] leading-[22px]">
                I understand and acknowledge the above disclaimer.
              </span>
            </label>

            {/* Actions */}
            <div className="flex flex-col gap-y-3">
              <PrimaryButton
                title="Download Certificate"
                disabled={!checked}
                isLoading={isConfirming}
                onClick={onConfirm}
                className="w-full"
              />
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="w-full py-2.5 font-clash-display font-semibold text-sm text-[#404040] hover:text-black transition-colors"
                >
                  Cancel
                </button>
              </Dialog.Close>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
